import { useDocumentsStore } from '~/stores/documents'
import { apiErrorMessage } from '~/utils/apiErrorMessage'
import type { SheetRecord } from '~~/shared/sheets/sheet'

/**
 * "New sheet from the current selection" — the ONE implementation both document surfaces use
 * (MAR-957; the filter itself is MAR-480's).
 *
 * Only DOCUMENTS go in the body. `validateIndexedDocuments` requires `type = 'document'` and 422s
 * the WHOLE request on any other id, and a selection routinely mixes folders in — so a mixed
 * selection would fail entirely rather than partially. The finder at `/app/docs` had this filter
 * and the sidebar did not, and the sidebar's call had no `try` either: a mixed selection failed
 * with nothing on screen and an unhandled rejection in the console.
 *
 * The selection is resolved through the store's cache rather than one surface's listing, so it
 * holds in every mode — a grouped sidebar's documents are not in the folder `items` at all. A row
 * the cache does not hold (an optimistic `upload-…` placeholder) is not a document yet and is left
 * out.
 *
 * `error` is for the caller to render inline; toasts are not a thing in this app (DET-WORK-039).
 */
export function useCreateSheetFromSelection() {
  const { t } = useI18n()
  const router = useRouter()
  const { apiFetch } = useApi()
  const documents = useDocumentsStore()

  const documentIds = computed(() =>
    [...documents.selectedIds].filter(id => documents.get(id)?.type === 'document'))
  const creating = ref(false)
  const error = ref('')

  async function create() {
    const itemIds = documentIds.value
    if (itemIds.length === 0 || creating.value) return
    creating.value = true
    error.value = ''
    try {
      const sheet = await apiFetch<SheetRecord>('/api/sheets', {
        method: 'POST',
        body: { name: t('sheets.untitledSheet'), itemIds },
      })
      documents.clearSelection()
      router.push(`/app/sheets/${sheet.id}`)
    } catch (err: any) {
      error.value = apiErrorMessage(err, t('documents.sheetCreateFailed'))
    } finally {
      creating.value = false
    }
  }

  return { documentIds, creating, error, create }
}
